import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Users, Edit, Trash2, ArrowLeft, ChevronRight, ChevronDown, FolderTree, MapPin, Upload } from 'lucide-react';

// Recursive tree node component
const DivisionNode = ({ division, depth, onEdit, onDelete, onAddSub, onManualUpload, onNavigate, t }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = division.children && division.children.length > 0;

  return (
    <div data-testid={`division-${division.id}`}>
      <div
        className={`flex items-center justify-between p-3 rounded-lg border transition-colors hover:bg-gray-50 ${depth === 0 ? 'bg-white shadow-sm' : 'bg-gray-50/50'}`}
        style={{ marginLeft: depth * 24 }}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {hasChildren ? (
            <button onClick={() => setExpanded(!expanded)} className="p-1 hover:bg-gray-200 rounded" data-testid={`toggle-${division.id}`}>
              {expanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
            </button>
          ) : (
            <span className="w-6" />
          )}
          <MapPin className={`w-4 h-4 shrink-0 ${depth === 0 ? 'text-blue-600' : depth === 1 ? 'text-green-600' : 'text-orange-500'}`} />
          <div className="min-w-0">
            <button onClick={() => onNavigate(division)} className="font-medium text-sm hover:text-blue-600 truncate block text-left">
              {division.name}
            </button>
            {division.description && (
              <p className="text-xs text-gray-500 truncate">{division.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2 ml-2 shrink-0">
            <Badge variant="outline" className="text-xs">
              <Users className="w-3 h-3 mr-1" />
              {division.workerCount || 0}
            </Badge>
            {hasChildren && (
              <Badge className="bg-blue-100 text-blue-700 text-xs">
                {division.children.length} {t('divisions.subdivisions')}
              </Badge>
            )}
            {depth > 0 && (
              <Badge variant="outline" className="text-xs text-gray-400">
                {t('divisions.level')} {depth + 1}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          <Button size="sm" variant="ghost" onClick={() => onAddSub(division)} title={t('divisions.addSubdivision')} data-testid={`add-sub-${division.id}`}>
            <Plus className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onEdit(division)} data-testid={`edit-${division.id}`}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => onDelete(division)} data-testid={`delete-${division.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {expanded && hasChildren && (
        <div className="mt-1 space-y-1">
          {division.children.map(child => (
            <DivisionNode
              key={child.id}
              division={child}
              depth={depth + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddSub={onAddSub}
              onManualUpload={onManualUpload}
              onNavigate={onNavigate}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Divisions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tree, setTree] = useState([]);
  const [flatDivisions, setFlatDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDivision, setEditingDivision] = useState(null);
  const [parentDivision, setParentDivision] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });

  useEffect(() => {
    loadTree();
  }, []);

  const loadTree = async () => {
    try {
      setLoading(true);
      const [treeRes, flatRes] = await Promise.all([
        axiosInstance.get('/divisions/tree'),
        axiosInstance.get('/divisions'),
      ]);
      setTree(treeRes.data);
      setFlatDivisions(flatRes.data);
    } catch (error) {
      toast.error(t('divisions.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingDivision(null);
    setParentDivision(null);
    setFormData({ name: '', description: '' });
    setDialogOpen(true);
  };

  const handleAddSub = (parent) => {
    setEditingDivision(null);
    setParentDivision(parent);
    setFormData({ name: '', description: '' });
    setDialogOpen(true);
  };

  const handleEdit = (division) => {
    setEditingDivision(division);
    setParentDivision(null);
    setFormData({ name: division.name, description: division.description || '' });
    setDialogOpen(true);
  };

  const handleDelete = async (division) => {
    // Check if it has children
    const hasChildren = division.children && division.children.length > 0;
    const msg = hasChildren
      ? t('divisions.deleteWithChildren', { name: division.name, count: division.children.length })
      : t('divisions.confirmDelete', { name: division.name });
    if (!window.confirm(msg)) return;

    try {
      await axiosInstance.delete(`/divisions/${division.id}`);
      toast.success(t('divisions.deleted'));
      loadTree();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error(t('divisions.nameRequired'));
      return;
    }

    try {
      if (editingDivision) {
        await axiosInstance.put(`/divisions/${editingDivision.id}`, formData);
        toast.success(t('divisions.updated'));
      } else {
        const payload = {
          ...formData,
          parentDivisionId: parentDivision?.id || null,
        };
        await axiosInstance.post('/divisions', payload);
        toast.success(parentDivision ? t('divisions.subdivisionCreated') : t('divisions.created'));
      }
      setDialogOpen(false);
      loadTree();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleNavigate = (division) => {
    navigate(`/divisions/${division.id}/analytics`);
  };

  // Count total divisions recursively
  const countAll = (nodes) => {
    let count = 0;
    for (const n of nodes) {
      count += 1;
      if (n.children) count += countAll(n.children);
    }
    return count;
  };

  const totalDivisions = countAll(tree);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t('common.back')}
      </Button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2" data-testid="divisions-title">
            <FolderTree className="w-7 h-7 text-blue-600" />
            {t('divisions.title')}
          </h1>
          <p className="text-gray-600 mt-1">
            {t('divisions.subtitle')} — {totalDivisions} {t('divisions.total')}
          </p>
        </div>
        <Button onClick={handleCreate} data-testid="create-division-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t('divisions.createDivision')}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>
      ) : tree.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <FolderTree className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-600">{t('divisions.noDivisions')}</p>
            <p className="text-sm text-gray-500 mt-1">{t('divisions.noDivisionsDesc')}</p>
            <Button onClick={handleCreate} className="mt-4">
              <Plus className="w-4 h-4 mr-2" />
              {t('divisions.createFirst')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-4 space-y-2">
            {tree.map(div => (
              <DivisionNode
                key={div.id}
                division={div}
                depth={0}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onAddSub={handleAddSub}
                onManualUpload={() => {}}
                onNavigate={handleNavigate}
                t={t}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle data-testid="division-dialog-title">
              {editingDivision
                ? t('divisions.editDivision')
                : parentDivision
                  ? t('divisions.addSubdivisionTo', { name: parentDivision.name })
                  : t('divisions.createDivision')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {parentDivision && (
                <div className="p-3 bg-blue-50 rounded-lg text-sm">
                  <span className="text-blue-600 font-medium">{t('divisions.parentLabel')}:</span> {parentDivision.name}
                </div>
              )}
              <div>
                <Label>{t('common.name')} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={parentDivision ? t('divisions.subNamePlaceholder') : t('divisions.namePlaceholder')}
                  data-testid="division-name-input"
                />
              </div>
              <div>
                <Label>{t('common.description')}</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('divisions.descPlaceholder')}
                  rows={3}
                  data-testid="division-desc-input"
                />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" data-testid="division-submit-btn">
                {editingDivision ? t('common.update') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Divisions;
